/**
 * The site name, shown in the immersive scene.
 *
 * A headset hides the page around the canvas, so the branding in the top bar goes with
 * it. This puts it back as a piece of the room: hung above and beyond where the model
 * sits, dim enough to read as signage rather than as part of what you came to look at.
 *
 * The ink is repaintable because the sky behind it is: pale lettering that reads as
 * restrained against a near-black sky is very nearly invisible against a light one.
 */

import {
  CanvasTexture,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  SRGBColorSpace,
} from 'three';

export const SITE_NAME = '3DPreview.eu';

const CANVAS_WIDTH_PX = 1024;
const CANVAS_HEIGHT_PX = 192;

const SIGN_WIDTH_M = 0.9;
const SIGN_HEIGHT_M = (SIGN_WIDTH_M * CANVAS_HEIGHT_PX) / CANVAS_WIDTH_PX;

/** Placed past the model and above eye level, so it never competes with the model. */
const SIGN_DISTANCE_M = 2.4;
const SIGN_HEIGHT_ABOVE_FLOOR_M = 2.1;

/** What the sign looks like against one particular sky. */
export interface SignInk {
  readonly colour: string;
  /**
   * How strongly it is laid over the sky, 0 to 1.
   *
   * Part of the ink rather than fixed, because the two skies are not symmetrical. Light
   * lettering over a near-black sky can be held well back and still separate from it,
   * while dark lettering over a light sky blends towards that sky as it fades — at half
   * strength the darkest possible ink is still barely distinguishable. Carrying its own
   * strength is what lets the sign be equally legible, and equally understated, in both.
   */
  readonly opacity: number;
}

export class Sign {
  readonly mesh: Mesh<PlaneGeometry, MeshBasicMaterial>;

  private readonly canvas: HTMLCanvasElement;
  private readonly texture: CanvasTexture;

  constructor(ink: SignInk) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = CANVAS_WIDTH_PX;
    this.canvas.height = CANVAS_HEIGHT_PX;
    this.paint(ink.colour);

    this.texture = new CanvasTexture(this.canvas);
    this.texture.colorSpace = SRGBColorSpace;
    this.texture.minFilter = LinearFilter;
    this.texture.generateMipmaps = false;

    this.mesh = new Mesh(
      new PlaneGeometry(SIGN_WIDTH_M, SIGN_HEIGHT_M),
      new MeshBasicMaterial({
        map: this.texture,
        transparent: true,
        opacity: ink.opacity,
        // Unlit, so the scene's lighting cannot leave it in shadow; and it writes no depth,
        // which keeps its transparent edges from punching holes in whatever is behind.
        depthWrite: false,
      }),
    );

    this.mesh.name = 'sign';
    this.mesh.position.set(0, SIGN_HEIGHT_ABOVE_FLOOR_M, -SIGN_DISTANCE_M);
  }

  get visible(): boolean {
    return this.mesh.visible;
  }

  set visible(visible: boolean) {
    this.mesh.visible = visible;
  }

  /**
   * Repaint for a different sky.
   *
   * The same canvas is drawn over again and the texture marked stale, rather than a new
   * texture being built and the old one disposed of: the sign is one line of text, and
   * swapping the map on a live material is more that can go wrong than redrawing it.
   */
  setInk(ink: SignInk): void {
    this.paint(ink.colour);
    this.texture.needsUpdate = true;
    this.mesh.material.opacity = ink.opacity;
  }

  private paint(colour: string): void {
    const context = this.canvas.getContext('2d');
    if (!context) throw new Error('Could not get a 2D context to draw the sign.');

    context.clearRect(0, 0, CANVAS_WIDTH_PX, CANVAS_HEIGHT_PX);
    context.fillStyle = colour;
    context.font = '600 104px system-ui, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(SITE_NAME, CANVAS_WIDTH_PX / 2, CANVAS_HEIGHT_PX / 2 + 4);
  }
}
